import { useMemo, useCallback, useEffect, useState } from 'react';
import { useApiCall } from './useApi';
import { ApiError, formatApiError, useApiClient } from '../api/apiClient';
import { useClient } from '../context/ClientContext';
import { useAllFindings } from '../context/FindingsContext';
import { normalizeFinding, normalizeSeverity, severityToApiValue, statusToApiValue } from '../utils/severity';
import {
  isADFinding,
  isCloudFinding,
  isDastFinding,
  isOsintFinding,
  isSddFinding,
  isNessusScanFinding,
  isNessusVulnerabilityFinding,
  isShodanVulnerabilityFinding,
  isEndpointVulnerabilityFinding,
  isVulnerabilityListFinding,
} from '../utils/findingClassification';
import { filterSortAndPageFindings } from '../utils/findingsListFiltering';
import { normalizeFindingStatusHistory } from '../utils/findingStatusHistory';
import { normalizeAssetRelationshipGraph } from '../utils/assetRelationships';
import { redactDemoObject, redactDemoText } from '../utils/demoRedaction';
import { demoAdFindings, demoAttackChains, isDemoEnvironmentClient } from '../data/demoAdSeed';

// Safely extract an array from API response — handles raw arrays,
// wrapped responses ({ items: [], findings: [], etc. }), and non-array values
function toArray(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    // Check common wrapper keys
    for (const key of ['items', 'findings', 'results', 'data', 'hosts', 'paths', 'reports', 'assets', 'clients', 'alerts', 'chains', 'frameworks', 'documents', 'history', 'events', 'runs']) {
      if (Array.isArray(data[key])) return data[key];
    }
  }
  return [];
}

function problemErrorFromPayload(data, fallbackPath) {
  if (!data || Array.isArray(data) || typeof data !== 'object') return null;
  const status = Number(data.status ?? data.Status);
  const hasProblemShape = data.type || data.Type || data.title || data.Title || data.detail || data.Detail;
  if (!hasProblemShape || !Number.isFinite(status) || status < 400) return null;

  return new ApiError(
    data.title || data.Title || data.detail || data.Detail || 'The API returned a problem response.',
    {
      status,
      path: data.instance || data.Instance || fallbackPath,
      code: data.code || data.Code,
      requestId: data.requestId || data.RequestId || data.traceId || data.TraceId,
      body: data,
      details: data.detail || data.Detail,
    },
  );
}

const EMPTY_SEVERITY_COUNTS = Object.freeze({ critical: 0, high: 0, medium: 0, low: 0, info: 0 });

function firstPresent(...values) {
  return values.find(v => v !== undefined && v !== null && v !== '');
}

function toNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toBool(value, fallback = false) {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

function toObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function normalizeWarnings(value) {
  return toArray(value)
    .map(item => typeof item === 'string'
      ? item
      : firstPresent(item?.message, item?.warning, item?.detail, item?.title))
    .filter(Boolean);
}

function normalizeSeverityCounts(value) {
  const source = toObject(value);
  return {
    critical: toNumber(firstPresent(source.critical, source.Critical), 0),
    high: toNumber(firstPresent(source.high, source.High), 0),
    medium: toNumber(firstPresent(source.medium, source.Medium), 0),
    low: toNumber(firstPresent(source.low, source.Low), 0),
    info: toNumber(firstPresent(source.info, source.informational, source.Info, source.Informational), 0),
  };
}

function normalizeCountMap(value) {
  return Object.entries(toObject(value)).reduce((acc, [key, count]) => {
    if (key) acc[key] = toNumber(count, 0);
    return acc;
  }, {});
}

function normalizeSummaryMeta(data, type) {
  const source = toObject(data);
  return {
    generatedAt: toIsoOrNull(firstPresent(source.generatedAt, source.GeneratedAt)),
    source: firstPresent(source.source, source.Source, type),
    sourceScanJobId: firstPresent(source.sourceScanJobId, source.SourceScanJobId, null),
    isStale: toBool(firstPresent(source.isStale, source.IsStale), false),
    warnings: normalizeWarnings(firstPresent(source.warnings, source.Warnings)),
  };
}

function rawPayload(data) {
  return data && typeof data === 'object' ? data : {};
}

function normalizeHookOptions(options) {
  if (typeof options === 'boolean') return { enabled: options };
  return options && typeof options === 'object' ? options : {};
}

function withSummaryMeta(normalized, data, type) {
  const meta = normalizeSummaryMeta(data, type);
  return {
    ...normalized,
    ...meta,
    meta,
  };
}

function normalizeSummaryGroup(item, index) {
  const source = toObject(item);
  return {
    ...source,
    id: firstPresent(source.id, source.groupId, source.key, source.type, source.dataType, source.category, source.domain, source.ipAddress, source.ip, `group-${index}`),
    key: firstPresent(source.key, source.groupKey, source.id, source.type, source.dataType, source.category, source.domain, source.ipAddress, source.ip, `group-${index}`),
    label: firstPresent(source.label, source.name, source.title, source.dataType, source.category, source.type, source.domain, source.ipAddress, source.ip, 'Grouped finding'),
    count: toNumber(firstPresent(source.count, source.total, source.findingCount, source.totalFindings, source.findings), 0),
    maxSeverity: firstPresent(source.maxSeverity, source.highestSeverity, source.sev, typeof source.severity === 'string' || typeof source.severity === 'number' ? source.severity : undefined),
    severity: normalizeSeverityCounts(firstPresent(source.severity, source.severityCounts, source.counts)),
  };
}

function summaryGroupSeverity(group) {
  const explicit = firstPresent(group.maxSeverity, group.highestSeverity, group.sev);
  if (explicit !== undefined) return normalizeSeverity(explicit).toLowerCase();
  if (typeof group.severity === 'string' || typeof group.severity === 'number') return normalizeSeverity(group.severity).toLowerCase();

  const counts = group.severity ?? {};
  if (toNumber(counts.critical) > 0) return 'critical';
  if (toNumber(counts.high) > 0) return 'high';
  if (toNumber(counts.medium) > 0) return 'medium';
  if (toNumber(counts.low) > 0) return 'low';
  return 'info';
}

function summaryGroupToFinding(group, index, kind) {
  const title = firstPresent(group.label, group.name, group.title, kind === 'sdd' ? 'Sensitive data finding' : 'OSINT exposure');
  const id = firstPresent(group.findingId, group.persistedFindingId, group.id, `${kind}-summary-${index}`);
  const resource = firstPresent(group.resource, group.resourcePath, group.path, group.location, group.domain, group.ipAddress, group.ip, '');
  const category = firstPresent(group.category, group.dataType, group.type, '');
  const additionalData = {
    ...(group.additionalData ?? group.metadata ?? {}),
    category,
    dataType: firstPresent(group.dataType, group.type, group.category, ''),
    resourcePath: resource,
  };

  return {
    ...group,
    id,
    findingId: id,
    persistedFindingId: id,
    title,
    sev: summaryGroupSeverity(group),
    status: firstPresent(group.status, group.state, 'open'),
    module: firstPresent(group.module, group.sourceModule, group.source, kind === 'sdd' ? 'sdd-summary' : 'osint-summary'),
    asset: firstPresent(group.asset, group.target, group.domain, group.ipAddress, group.ip, resource),
    resource,
    category,
    age: firstPresent(group.age, group.lastSeenAt, group.updatedAt, group.createdAt, group.discoveredAt, null),
    duplicateGroupKey: firstPresent(group.duplicateGroupKey, group.groupKey, group.key, `${kind}:${title}`),
    summaryCount: toNumber(group.count, 1),
    additionalData,
    detail: group.detail ?? { resourcePath: resource, additionalData },
    __summaryOnly: true,
  };
}

function normalizeReportSummaryItem(item, index) {
  const source = toObject(item);
  const reportType = normalizeReportType(firstPresent(source.reportType, source.type, source.category));
  const formats = toArray(firstPresent(source.formats, source.availableFormats))
    .map(format => String(format).toLowerCase())
    .filter(Boolean);
  return {
    ...source,
    id: firstPresent(source.id, source.reportId, source.templateId, source.type, `report-${index}`),
    title: firstPresent(source.title, source.name, source.reportName, source.type, 'Report'),
    reportType,
    type: reportType,
    generatedAt: toIsoOrNull(firstPresent(source.generatedAt, source.createdAt, source.updatedAt, source.publishedAt)),
    status: firstPresent(source.status, source.state, 'available'),
    availableFormats: formats,
    formats,
  };
}

const REPORT_TYPE_NAMES = {
  0: 'monthly',
  1: 'qbr',
  2: 'compliance',
  3: 'pentest',
  4: 'snapshot',
};

function normalizeReportType(value) {
  if (value === undefined || value === null || value === '') return 'monthly';
  if (typeof value === 'number') return REPORT_TYPE_NAMES[value] ?? 'monthly';
  const raw = String(value).trim().toLowerCase();
  if (/^\d+$/.test(raw)) return REPORT_TYPE_NAMES[Number(raw)] ?? 'monthly';
  if (raw === 'quarterly' || raw === 'qbrdeck') return 'qbr';
  return raw.replace(/\d+/g, '') || 'monthly';
}

function normalizeReportTypeCounts(value) {
  if (Array.isArray(value)) {
    return value.map(item => {
      const source = toObject(item);
      const reportType = normalizeReportType(firstPresent(source.reportType, source.type, source.category));
      return {
        ...source,
        reportType,
        type: reportType,
        count: toNumber(source.count, 0),
      };
    });
  }

  return Object.entries(normalizeCountMap(value)).map(([reportType, count]) => ({
    reportType: normalizeReportType(reportType),
    type: normalizeReportType(reportType),
    count,
  }));
}

function normalizeFrameworkSummary(item, index) {
  if (typeof item === 'string' || typeof item === 'number') {
    const value = String(item);
    return {
      id: value || `framework-${index}`,
      name: value || 'Framework',
      canonicalName: value || `framework-${index}`,
      score: null,
      passing: 0,
      pass: 0,
      failing: 0,
      failingControls: 0,
      evidenceSatisfiedControls: 0,
      untested: 0,
      untestedControls: 0,
      total: 0,
      status: 'active',
    };
  }

  const source = toObject(item);
  const total = toNumber(firstPresent(source.total, source.totalControls, source.controlCount), 0);
  const failingControls = toNumber(firstPresent(source.failingControls, source.failing, source.failed, source.failCount), 0);
  const evidenceSatisfiedControls = toNumber(firstPresent(source.evidenceSatisfiedControls, source.metByEvidenceControls, source.metByEvidence, source.evidenceSatisfied), 0);
  const untestedControls = toNumber(firstPresent(source.untestedControls, source.untested, source.unknown, source.untestedCount), 0);
  const passing = toNumber(firstPresent(source.passingControls, source.passing, source.passed, source.passCount), Math.max(0, total - failingControls - untestedControls));
  return {
    ...source,
    id: firstPresent(source.id, source.frameworkId, source.canonicalName, source.name, source.frameworkName, source.framework, `framework-${index}`),
    name: firstPresent(source.displayName, source.name, source.frameworkName, source.framework, source.title, source.canonicalName, source.frameworkId, 'Framework'),
    canonicalName: firstPresent(source.canonicalName, source.frameworkId, source.id, source.name, source.frameworkName, source.framework, `framework-${index}`),
    score: toNumber(firstPresent(source.score, source.percent, source.complianceScore), 0),
    passing,
    pass: passing,
    failing: failingControls,
    failingControls,
    evidenceSatisfiedControls,
    untested: untestedControls,
    untestedControls,
    total,
  };
}

export const emptyDashboardSummary = Object.freeze(withSummaryMeta({
  client: null,
  score: { current: null, trend: [] },
  findings: { total: 0, open: 0, openCriticalHigh: 0, severity: EMPTY_SEVERITY_COUNTS, openSeverity: EMPTY_SEVERITY_COUNTS },
  sensitiveData: { total: 0, groups: 0, open: 0 },
  osint: { total: 0, breachRecords: 0, exposedCredentials: 0, domainMentions: 0 },
  assets: { total: 0, externalIps: 0, internalHosts: 0, domains: 0, cloudResources: 0, accounts: 0 },
  recentFindings: [],
  alerts: [],
  compliance: { averageScore: null, failing: 0, totalControls: 0 },
  attackPaths: { total: 0, active: 0, top: [] },
}, null, 'dashboard'));

export const emptySddSummary = Object.freeze(withSummaryMeta({
  total: 0,
  totalFindings: 0,
  visibleGroupCount: 0,
  severity: EMPTY_SEVERITY_COUNTS,
  severityCounts: EMPTY_SEVERITY_COUNTS,
  dataTypeCounts: {},
  rows: [],
  groups: [],
  topGroups: [],
  raw: {},
}, null, 'sdd'));

export const emptyOsintSummary = Object.freeze(withSummaryMeta({
  total: 0,
  totalFindings: 0,
  breachRecords: 0,
  exposedCredentials: 0,
  domainMentions: 0,
  visibleGroupCount: 0,
  filteredNoiseCount: 0,
  attackerVisibleMap: null,
  rows: [],
  groups: [],
  topGroups: [],
  raw: {},
}, null, 'osint'));

export const emptyReportsSummary = Object.freeze(withSummaryMeta({
  totalReports: 0,
  totalCurrentReports: 0,
  totalVersionsAvailable: 0,
  reportTypeCount: 0,
  reportTypeCounts: {},
  typeCounts: [],
  latestPerType: [],
  latestReports: [],
  reports: [],
  history: [],
  latestGeneratedAt: null,
  raw: {},
}, null, 'reports'));

export const emptyComplianceSummary = Object.freeze(withSummaryMeta({
  headline: {
    activeFrameworks: 0,
    totalControls: 0,
    passingControls: 0,
    failingControls: 0,
    evidenceSatisfiedControls: 0,
    untestedControls: 0,
    averageScore: null,
  },
  activeFrameworks: 0,
  totalControls: 0,
  passing: 0,
  passingControls: 0,
  failing: 0,
  failingControls: 0,
  evidenceSatisfiedControls: 0,
  untested: 0,
  untestedControls: 0,
  averageScore: null,
  frameworks: [],
  latestReport: null,
  raw: {},
}, null, 'compliance'));

export function normalizeDashboardSummary(data) {
  const source = toObject(data);
  const findings = toObject(firstPresent(source.findings, source.findingSummary));
  const score = toObject(firstPresent(source.score, source.securityScore));
  const sensitiveData = toObject(firstPresent(source.sensitiveData, source.sdd));
  const osint = toObject(source.osint);
  const assets = toObject(source.assets);
  const compliance = toObject(source.compliance);
  const attackPaths = toObject(firstPresent(source.attackPaths, source.attackChains));

  return withSummaryMeta({
    client: firstPresent(source.client, source.clientSummary, null),
    score: {
      current: firstPresent(score.current, score.value, score.score, source.scoreValue, null),
      trend: toArray(firstPresent(score.trend, source.scoreTrend)),
    },
    findings: {
      total: toNumber(firstPresent(findings.total, source.totalFindings), 0),
      open: toNumber(firstPresent(findings.open, findings.openFindings), 0),
      openCriticalHigh: toNumber(firstPresent(findings.openCriticalHigh, findings.criticalHighOpen), 0),
      severity: normalizeSeverityCounts(firstPresent(findings.severity, findings.severityCounts)),
      openSeverity: normalizeSeverityCounts(firstPresent(findings.openSeverity, findings.openSeverityCounts)),
    },
    sensitiveData: {
      total: toNumber(firstPresent(sensitiveData.total, sensitiveData.totalFindings, sensitiveData.findings), 0),
      groups: toNumber(firstPresent(sensitiveData.groups, sensitiveData.visibleGroupCount, sensitiveData.groupCount), 0),
      open: toNumber(firstPresent(sensitiveData.open, sensitiveData.openFindings), 0),
    },
    osint: {
      total: toNumber(firstPresent(osint.total, osint.totalFindings), 0),
      breachRecords: toNumber(firstPresent(osint.breachRecords, osint.breaches), 0),
      exposedCredentials: toNumber(firstPresent(osint.exposedCredentials, osint.credentials), 0),
      domainMentions: toNumber(firstPresent(osint.domainMentions, osint.domains), 0),
    },
    assets: {
      total: toNumber(firstPresent(assets.total, assets.totalAssets), 0),
      externalIps: toNumber(firstPresent(assets.externalIps, assets.externalIPs), 0),
      internalHosts: toNumber(assets.internalHosts, 0),
      domains: toNumber(assets.domains, 0),
      cloudResources: toNumber(assets.cloudResources, 0),
      accounts: toNumber(assets.accounts, 0),
    },
    recentFindings: toArray(firstPresent(source.recentFindings, source.topFindings)).map(normalizeFinding).filter(Boolean),
    alerts: toArray(firstPresent(source.alerts, source.topAlerts)).map(normalizeAlert).filter(Boolean),
    compliance: {
      averageScore: firstPresent(compliance.averageScore, compliance.score, null),
      failing: toNumber(firstPresent(compliance.failing, compliance.failed), 0),
      totalControls: toNumber(firstPresent(compliance.totalControls, compliance.controls), 0),
    },
    attackPaths: {
      total: toNumber(firstPresent(attackPaths.total, attackPaths.totalPaths, attackPaths.totalChains), 0),
      active: toNumber(firstPresent(attackPaths.active, attackPaths.activePaths, attackPaths.activeChains), 0),
      top: toArray(firstPresent(attackPaths.top, attackPaths.paths, attackPaths.chains)),
    },
  }, data, 'dashboard');
}

export function normalizeSddSummary(data) {
  const source = toObject(data);
  const groups = toArray(firstPresent(source.groups, source.topGroups, source.rows, source.items)).map(normalizeSummaryGroup);
  const rows = groups.map((group, index) => summaryGroupToFinding(group, index, 'sdd'));
  const severityCounts = normalizeSeverityCounts(firstPresent(source.severityCounts, source.severity));
  const totalFindings = toNumber(firstPresent(source.totalFindings, source.total, source.findings), rows.reduce((sum, row) => sum + toNumber(row.count, 1), 0));
  return withSummaryMeta({
    total: totalFindings,
    totalFindings,
    visibleGroupCount: toNumber(firstPresent(source.visibleGroupCount, source.groupCount, source.groupsCount), groups.length),
    severity: severityCounts,
    severityCounts,
    dataTypeCounts: normalizeCountMap(firstPresent(source.dataTypeCounts, source.typeCounts)),
    rows,
    groups,
    topGroups: groups,
    raw: rawPayload(data),
  }, data, 'sdd');
}

export function normalizeOsintSummary(data) {
  const source = toObject(data);
  const groups = toArray(firstPresent(source.groups, source.topGroups, source.rows, source.items)).map(normalizeSummaryGroup);
  const rows = groups.map((group, index) => summaryGroupToFinding(group, index, 'osint'));
  const total = toNumber(firstPresent(source.total, source.totalFindings, source.findingCount), rows.reduce((sum, row) => sum + toNumber(row.count, 1), 0));
  return withSummaryMeta({
    total,
    totalFindings: total,
    breachRecords: toNumber(firstPresent(source.breachRecords, source.breaches), 0),
    exposedCredentials: toNumber(firstPresent(source.exposedCredentials, source.credentials), 0),
    domainMentions: toNumber(firstPresent(source.domainMentions, source.domains), 0),
    visibleGroupCount: toNumber(firstPresent(source.visibleGroupCount, source.groupCount, source.groupsCount), groups.length),
    filteredNoiseCount: toNumber(firstPresent(source.filteredNoiseCount, source.noiseFiltered), 0),
    attackerVisibleMap: firstPresent(source.attackerVisibleMap, source.map, null),
    rows,
    groups,
    topGroups: groups,
    raw: rawPayload(data),
  }, data, 'osint');
}

export function normalizeReportsSummary(data) {
  const source = toObject(data);
  const latestReports = toArray(firstPresent(source.latestReports, source.latest)).map(normalizeReportSummaryItem);
  const reports = toArray(firstPresent(source.reports, source.history, source.items)).map(normalizeReportSummaryItem);
  const typeCounts = normalizeReportTypeCounts(firstPresent(source.typeCounts, source.reportTypeCounts));
  const totalReports = toNumber(firstPresent(source.totalReports, source.totalCurrentReports, source.currentReports, source.total), reports.length);
  const latestPerType = toArray(firstPresent(source.latestPerType, source.latestReports, source.latest)).map(normalizeReportSummaryItem);
  return withSummaryMeta({
    totalReports,
    totalCurrentReports: totalReports,
    totalVersionsAvailable: toNumber(firstPresent(source.totalVersionsAvailable, source.totalVersions, source.versions), reports.length),
    reportTypeCount: toNumber(firstPresent(source.reportTypeCount, source.typeCount), typeCounts.length),
    typeCounts,
    latestPerType,
    reportTypeCounts: normalizeCountMap(firstPresent(source.reportTypeCounts, source.typeCounts)),
    latestReports,
    reports,
    history: reports,
    latestGeneratedAt: firstPresent(source.latestGeneratedAt, latestReports[0]?.generatedAt, reports[0]?.generatedAt, null),
    raw: rawPayload(data),
  }, data, 'reports');
}

export function normalizeComplianceSummary(data) {
  const source = toObject(data);
  const headlineSource = toObject(source.headline);
  const controls = toObject(firstPresent(source.controls, source.controlSummary, headlineSource));
  const frameworks = toArray(firstPresent(source.frameworks, source.activeFrameworks, source.items)).map(normalizeFrameworkSummary);
  const activeFrameworks = toNumber(firstPresent(source.activeFrameworks, source.frameworkCount, headlineSource.activeFrameworks), frameworks.length);
  const totalControls = toNumber(firstPresent(source.totalControls, controls.totalControls, controls.total), 0);
  const passingControls = toNumber(firstPresent(source.passingControls, source.passing, controls.passingControls, controls.passing, controls.passed), 0);
  const failingControls = toNumber(firstPresent(source.failingControls, source.failing, controls.failingControls, controls.failing, controls.failed), 0);
  const evidenceSatisfiedControls = toNumber(firstPresent(source.evidenceSatisfiedControls, source.metByEvidenceControls, source.metByEvidence, source.evidenceSatisfied, controls.evidenceSatisfiedControls, controls.metByEvidenceControls), 0);
  const untestedControls = toNumber(firstPresent(source.untestedControls, source.untested, controls.untestedControls, controls.untested), 0);
  const averageScore = firstPresent(source.averageScore, source.score, controls.averageScore, null);
  const headline = {
    activeFrameworks,
    totalControls,
    passingControls,
    failingControls,
    evidenceSatisfiedControls,
    untestedControls,
    averageScore,
  };
  return withSummaryMeta({
    headline,
    activeFrameworks,
    totalControls,
    passing: passingControls,
    passingControls,
    failing: failingControls,
    failingControls,
    evidenceSatisfiedControls,
    untested: untestedControls,
    untestedControls,
    averageScore,
    frameworks,
    latestReport: firstPresent(source.latestReport, source.report, null),
    raw: rawPayload(data),
  }, data, 'compliance');
}

function useClientSummary(pathSuffix, normalizer, emptySummary, options = {}) {
  const { enabled = true } = normalizeHookOptions(options);
  const { clientId } = useClient();
  const path = enabled && clientId ? `/clients/${clientId}/${pathSuffix}` : null;
  const { data, loading, error, refetch } = useApiCall(path);
  const problemError = problemErrorFromPayload(data, path);
  const normalized = useMemo(() => {
    if (!enabled || !clientId || error || problemError) return emptySummary;
    return normalizer(data);
  }, [enabled, clientId, data, error, problemError, normalizer, emptySummary]);

  return {
    data: normalized,
    loading: enabled && clientId ? loading : false,
    error: enabled && clientId ? error || problemError : null,
    refetch,
    isStale: normalized.isStale,
    warnings: normalized.warnings,
  };
}


function toIsoOrNull(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : value;
}

function labelize(value, fallback = '') {
  if (value === undefined || value === null || value === '') return fallback;
  return String(value)
    .trim()
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function parseDetails(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function isOpaqueTag(value) {
  return typeof value === 'string' && (/^[a-f0-9]{32,64}$/i.test(value) || /^[0-9a-f-]{36}$/i.test(value));
}

function normalizeAssetRecord(asset) {
  if (!asset || typeof asset !== 'object') return asset;
  const redacted = redactDemoObject(asset);
  const tags = Array.isArray(redacted.tags)
    ? redacted.tags
        .map(tag => typeof tag === 'string' ? redactDemoText(tag) : tag)
        .filter(tag => !(typeof tag === 'string' && isOpaqueTag(tag)))
    : redacted.tags;
  return {
    ...redacted,
    id: asset.id,
    assetId: asset.assetId,
    tags,
  };
}

function normalizeScanRecord(scan) {
  if (!scan || typeof scan !== 'object') return scan;
  const redacted = redactDemoObject(scan);
  return {
    ...redacted,
    id: scan.id,
    scanId: scan.scanId,
    scanJobId: scan.scanJobId,
    jobId: scan.jobId,
  };
}

function inferSchedulerStatus(item, eventType, explanation) {
  const explicit = firstPresent(item.status, item.result, item.outcome, item.state);
  if (explicit) return explicit;

  const combined = `${eventType ?? ''} ${item.reasonCode ?? ''} ${explanation ?? ''}`.toLowerCase();
  if (combined.includes('fail') || combined.includes('error') || combined.includes('timeout') || combined.includes('unavailable')) return 'failed';
  if (combined.includes('skip') || combined.includes('paused') || combined.includes('disabled') || combined.includes('not_due')) return 'skipped';
  if (combined.includes('queue') || combined.includes('queued') || combined.includes('claim')) return 'queued';
  if (combined.includes('complete') || combined.includes('success')) return 'completed';
  if (combined.includes('run') || combined.includes('start')) return 'running';
  return 'recorded';
}

export function normalizeSchedulerHistory(data) {
  return toArray(data)
    .map((item, index) => {
      if (!item || typeof item !== 'object') return null;

      const occurredAt = toIsoOrNull(firstPresent(
        item.occurredAt,
        item.createdAt,
        item.timestamp,
        item.startedAt,
        item.completedAt,
        item.updatedAt
      ));
      const eventType = firstPresent(item.eventType, item.event, item.action, item.type);
      const explanation = firstPresent(item.explanation, item.reason, item.message, item.errorMessage, item.lastErrorMessage, item.details);
      const status = inferSchedulerStatus(item, eventType, explanation);
      const details = parseDetails(firstPresent(item.detailsJson, item.details));

      return {
        ...item,
        id: firstPresent(item.id, item.historyId, item.eventId, item.runId, item.scanJobId, item.jobId, `${occurredAt || 'scheduler'}-${index}`),
        occurredAt,
        eventLabel: labelize(eventType, 'Scheduler Event'),
        statusLabel: labelize(status, 'Recorded'),
        status,
        explanation: typeof explanation === 'string' ? redactDemoText(explanation) : '',
        profileName: redactDemoText(firstPresent(item.profileName, item.scanProfile, item.profile, '')),
        source: labelize(redactDemoText(firstPresent(item.source, item.trigger, item.triggeredBy, item.origin, item.actor)), 'Scheduler'),
        scanJobId: firstPresent(item.scanJobId, item.jobId, item.queuedJobId, item.runId, ''),
        scheduleId: firstPresent(item.scheduleId, item.scanScheduleId, ''),
        reasonCode: firstPresent(item.reasonCode, item.reason_code, item.code, ''),
        clientName: redactDemoText(firstPresent(item.clientName, item.client, '')),
        details: redactDemoObject(details),
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const at = a.occurredAt ? new Date(a.occurredAt).getTime() : 0;
      const bt = b.occurredAt ? new Date(b.occurredAt).getTime() : 0;
      return bt - at;
    });
}


export function useClientProfile() {
  const { clientId } = useClient();
  const { data, loading, error } = useApiCall(clientId ? `/clients/${clientId}/profile` : null);
  return { data: data ?? null, loading, error };
}

export function useClientScore() {
  const { clientId } = useClient();
  const { data, loading, error } = useApiCall(clientId ? `/clients/${clientId}/score` : null);
  return { data, loading, error };
}

export function useMetricsRollup() {
  const { clientId } = useClient();
  const { data, loading, error, refetch } = useApiCall(clientId ? `/clients/${clientId}/metrics/rollup` : null);
  return { data: data ?? null, loading, error, refetch };
}

export function useDashboardSummary(options = {}) {
  return useClientSummary('dashboard-summary', normalizeDashboardSummary, emptyDashboardSummary, options);
}

export function useReportsSummary(options = {}) {
  const normalizedOptions = normalizeHookOptions(options);
  const limit = Math.max(1, Math.min(Number(normalizedOptions.limit) || 24, 100));
  return useClientSummary(`reports-summary?limit=${encodeURIComponent(String(limit))}`, normalizeReportsSummary, emptyReportsSummary, normalizedOptions);
}

export function useSddSummary(options = {}) {
  const normalizedOptions = normalizeHookOptions(options);
  const limit = Math.max(1, Math.min(Number(normalizedOptions.limit) || 25, 100));
  return useClientSummary(`sdd-summary?limit=${encodeURIComponent(String(limit))}`, normalizeSddSummary, emptySddSummary, normalizedOptions);
}

export function useOsintSummary(options = {}) {
  const normalizedOptions = normalizeHookOptions(options);
  const limit = Math.max(1, Math.min(Number(normalizedOptions.limit) || 25, 100));
  return useClientSummary(`osint-summary?limit=${encodeURIComponent(String(limit))}`, normalizeOsintSummary, emptyOsintSummary, normalizedOptions);
}

export function useComplianceSummary(options = {}) {
  return useClientSummary('compliance-summary', normalizeComplianceSummary, emptyComplianceSummary, options);
}

export function useRecentFindings(limit = 10) {
  const { clientId } = useClient();
  const pageSize = Math.max(1, Math.min(Number(limit) || 10, 25));
  const path = clientId ? `/clients/${clientId}/findings?pageSize=${pageSize}&activeOnly=true` : null;
  const { data, loading, error, refetch } = useApiCall(path);
  const findings = useMemo(() => {
    const raw = Array.isArray(data?.findings) ? data.findings : toArray(data);
    return raw.map(normalizeFinding).filter(Boolean);
  }, [data]);
  return { data: findings, total: Number(data?.total ?? findings.length), loading, error, refetch };
}

export function useFindings(filters = {}) {
  const { findings: raw, loading, error, refetch, includeSuppressed } = useAllFindings();
  const { clientId } = useClient();
  // The dedicated vulnerability endpoint returns the complete approved scanner queue.
  // The all-findings page keeps using its broader provider cache.
  const useVulnCategoryApi = !filters.includeAll;
  const vulnParams = new URLSearchParams({ category: 'vuln', pageSize: '500' });
  if (includeSuppressed) vulnParams.set('includeSuppressed', 'true');
  const vulnPath = clientId && useVulnCategoryApi
    ? `/clients/${clientId}/findings?${vulnParams.toString()}`
    : null;
  const vulnCall = useApiCall(vulnPath);

  const result = useMemo(() => {
    const categoryRows = useVulnCategoryApi ? toArray(vulnCall.data) : [];
    const sourceRaw = useVulnCategoryApi ? categoryRows : raw;
    let scoped = sourceRaw.map(normalizeFinding).filter(Boolean).filter(f => !isSddFinding(f));

    // /client/findings includes scanner-origin vulnerability findings. CVE identifiers are shown when available.
    if (!filters.includeAll) {
      scoped = scoped.filter(isVulnerabilityListFinding);
    }

    const sourceRows = {
      NESSUS: scoped.filter(isNessusScanFinding),
      SHODAN: scoped.filter(isShodanVulnerabilityFinding),
      ENDPOINT: scoped.filter(isEndpointVulnerabilityFinding),
    };
    const summary = {
      total: scoped.length,
      open: scoped.filter(f => f.status === 'open').length,
      severity: scoped.reduce((acc, f) => {
        acc[f.sev] = (acc[f.sev] ?? 0) + 1;
        return acc;
      }, {}),
      source: Object.fromEntries(
        Object.entries(sourceRows).map(([source, rows]) => [source, rows.length]),
      ),
      sourceSeverity: Object.fromEntries(Object.entries(sourceRows).map(([source, rows]) => [
        source,
        Object.fromEntries(['critical', 'high', 'medium', 'low', 'info'].map(s => [s, rows.filter(f => f.sev === s).length])),
      ])),
    };

    const paged = filterSortAndPageFindings(scoped, filters);

    return { data: paged.data, total: paged.total, filteredOpen: paged.open, summary };
  }, [
    raw,
    vulnCall.data,
    useVulnCategoryApi,
    filters.includeAll,
    filters.severity,
    filters.status,
    filters.module,
    filters.latestScanJobId,
    filters.framework,
    filters.minEpss,
    filters.search,
    filters.sort,
    filters.page,
    filters.pageSize,
  ]);

  return {
    data: result.data,
    total: result.total,
    filteredOpen: result.filteredOpen,
    summary: result.summary,
    loading: useVulnCategoryApi ? vulnCall.loading : loading,
    error: useVulnCategoryApi ? vulnCall.error : error,
    refetch: useVulnCategoryApi ? vulnCall.refetch : refetch,
  };
}

export function useFinding(findingId, routeFallback = null) {
  const { clientId } = useClient();
  const path = clientId && findingId ? `/clients/${clientId}/findings/${findingId}` : null;
  const { data, loading, error, refetch } = useApiCall(path);
  const { findings: cachedFindings } = useAllFindings();
  const finding = data?.finding ?? data;
  const routeFallbackFinding = useMemo(() => {
    if (!routeFallback || typeof routeFallback !== 'object') return null;
    const ids = [routeFallback.id, routeFallback.findingId, routeFallback.persistedFindingId].filter(Boolean).map(String);
    return ids.includes(String(findingId)) ? routeFallback : null;
  }, [routeFallback, findingId]);
  const cachedFinding = useMemo(() => {
    const id = String(findingId || '');
    if (!id || !Array.isArray(cachedFindings)) return null;
    return cachedFindings.find(f => {
      const ids = [f?.id, f?.findingId, f?.persistedFindingId].filter(Boolean).map(String);
      return ids.includes(id);
    }) ?? null;
  }, [cachedFindings, findingId]);
  const fallbackFinding = useMemo(() => {
    if (!isDemoEnvironmentClient(clientId)) return null;
    return demoAdFindings.find(f => {
      const ids = [f.id, f.findingId, f.persistedFindingId].filter(Boolean).map(String);
      return ids.includes(String(findingId));
    }) ?? null;
  }, [clientId, findingId]);
  const resilientFinding = finding ?? routeFallbackFinding ?? cachedFinding ?? fallbackFinding;
  return {
    data: resilientFinding ? normalizeFinding(resilientFinding) : null,
    loading: loading && !routeFallbackFinding && !cachedFinding && !fallbackFinding,
    error: (routeFallbackFinding || cachedFinding || fallbackFinding) ? null : error,
    refetch,
  };
}

export function useFindingStatusHistory(findingId) {
  const { clientId } = useClient();
  const path = clientId && findingId ? `/clients/${clientId}/findings/${findingId}/history` : null;
  const { data, loading, error, refetch } = useApiCall(path);
  return {
    data: normalizeFindingStatusHistory(data),
    loading,
    error,
    refetch,
  };
}

export function useDuplicateFindingGroup(groupKey) {
  const { clientId } = useClient();
  const api = useApiClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const key = typeof groupKey === 'string' ? groupKey.trim() : '';

  useEffect(() => {
    let cancelled = false;
    if (!clientId || !key) {
      setData(null);
      setLoading(false);
      setError('');
      return () => { cancelled = true; };
    }

    setLoading(true);
    setError('');
    api.get(`/clients/${clientId}/findings/duplicate-groups/${encodeURIComponent(key)}`)
      .then(result => {
        if (cancelled) return;
        const members = toArray(result?.members ?? result).map(normalizeFinding).filter(Boolean);
        setData({
          ...result,
          duplicateGroupKey: result?.duplicateGroupKey ?? key,
          memberCount: result?.memberCount ?? members.length,
          affectedAssets: Array.isArray(result?.affectedAssets) ? result.affectedAssets : [],
          affectedAssetIds: Array.isArray(result?.affectedAssetIds) ? result.affectedAssetIds : [],
          members,
        });
      })
      .catch(err => {
        if (cancelled) return;
        if (err?.status === 404) {
          setData({ duplicateGroupKey: key, memberCount: 0, members: [], unavailable: true });
          setError('');
          return;
        }
        setData(null);
        setError(formatApiError(err, 'Duplicate group details are not available.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [clientId, key]);

  return { data, loading, error };
}

// AlertSeverity enum: Low=0, Medium=1, High=2, Critical=3 (different from findings Severity)
const ALERT_SEV_MAP = { 0: 'low', 1: 'medium', 2: 'high', 3: 'critical' };

function normalizeAlert(a) {
  if (!a) return null;
  const rawSev = a.severity ?? a.sev;
  const severity = typeof rawSev === 'number'
    ? (ALERT_SEV_MAP[rawSev] ?? 'info')
    : (typeof rawSev === 'string' ? rawSev.toLowerCase() : 'info');
  return {
    id: a.alertId ?? a.id ?? '',
    severity,
    msg: a.message ?? a.msg ?? '',
    client: a.clientName ?? a.client ?? '',
    time: a.createdAt ?? a.time ?? null,
    read: a.readAt != null || a.read === true,
    findingId: a.relatedFindingId ?? null,
  };
}

export function useClientAlerts() {
  const { clientId } = useClient();
  const { data, loading, error } = useApiCall(clientId ? `/clients/${clientId}/alerts` : null);
  const alerts = useMemo(() => toArray(data).map(normalizeAlert).filter(Boolean), [data]);
  return { data: alerts, loading, error };
}

export function useScoreHistory(enabled = true) {
  const { clientId } = useClient();
  const { data, loading, error } = useApiCall(clientId && enabled ? `/clients/${clientId}/score/history` : null);
  return { data: toArray(data), loading, error };
}

export function useFrameworks(enabled = true) {
  const { clientId } = useClient();
  const { data, loading, error } = useApiCall(clientId && enabled ? `/clients/${clientId}/compliance/frameworks` : null);
  return { data: toArray(data), loading, error };
}

function parseAnswers(value) {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string') return {};
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeComplianceControlReference(item, index) {
  const source = toObject(item);
  return {
    ...source,
    id: firstPresent(source.id, source.controlId, source.ControlId, `control-${index}`),
    nist53ControlId: firstPresent(source.nist53ControlId, source.Nist53ControlId, ''),
    frameworkId: firstPresent(source.frameworkId, source.FrameworkId, ''),
    controlId: firstPresent(source.controlId, source.ControlId, ''),
    controlName: firstPresent(source.controlName, source.ControlName, ''),
  };
}

export function normalizeComplianceEvidenceType(item, index) {
  const source = toObject(item);
  return {
    ...source,
    id: firstPresent(source.id, source.Id, `evidence-type-${index}`),
    label: firstPresent(source.label, source.Label, source.name, source.Name, 'Evidence type'),
    description: firstPresent(source.description, source.Description, ''),
    requiredAttestations: toArray(firstPresent(source.requiredAttestations, source.RequiredAttestations)).map((attestation, attestationIndex) => {
      const a = toObject(attestation);
      return {
        ...a,
        id: firstPresent(a.id, a.Id, `attestation-${attestationIndex}`),
        label: firstPresent(a.label, a.Label, a.text, a.Text, 'Required attestation'),
      };
    }),
    nist53ControlIds: toArray(firstPresent(source.nist53ControlIds, source.Nist53ControlIds)).map(String),
    mappedControls: toArray(firstPresent(source.mappedControls, source.MappedControls)).map(normalizeComplianceControlReference),
  };
}

export function normalizeComplianceEvidenceDocument(item, index) {
  const source = toObject(item);
  const evidenceDocumentId = firstPresent(source.evidenceDocumentId, source.EvidenceDocumentId, source.id, `evidence-${index}`);
  const archivedAt = firstPresent(source.archivedAt, source.ArchivedAt, null);
  const archivedFlag = toBool(firstPresent(source.archived, source.Archived), false);
  const archived = archivedFlag || Boolean(archivedAt);
  return {
    ...source,
    id: evidenceDocumentId,
    evidenceDocumentId,
    clientId: firstPresent(source.clientId, source.ClientId, ''),
    mspId: firstPresent(source.mspId, source.MspId, ''),
    evidenceType: firstPresent(source.evidenceType, source.EvidenceType, ''),
    evidenceTypeLabel: firstPresent(source.evidenceTypeLabel, source.EvidenceTypeLabel, source.evidenceType, source.EvidenceType, 'Evidence'),
    originalFileName: firstPresent(source.originalFileName, source.OriginalFileName, 'evidence-document'),
    storedFileName: firstPresent(source.storedFileName, source.StoredFileName, ''),
    contentType: firstPresent(source.contentType, source.ContentType, ''),
    originalSizeBytes: toNumber(firstPresent(source.originalSizeBytes, source.OriginalSizeBytes), 0),
    compressedSizeBytes: toNumber(firstPresent(source.compressedSizeBytes, source.CompressedSizeBytes), 0),
    sha256Hex: firstPresent(source.sha256Hex, source.Sha256Hex, ''),
    answers: parseAnswers(firstPresent(source.answersJson, source.AnswersJson, source.answers, source.Answers)),
    uploadedBy: firstPresent(source.uploadedBy, source.UploadedBy, ''),
    uploadedAt: firstPresent(source.uploadedAt, source.UploadedAt, null),
    archivedAt,
    archivedBy: firstPresent(source.archivedBy, source.ArchivedBy, ''),
    archiveReason: firstPresent(source.archiveReason, source.ArchiveReason, ''),
    archived,
    mappedControls: toArray(firstPresent(source.mappedControls, source.MappedControls)).map(normalizeComplianceControlReference),
  };
}

export function useComplianceEvidenceTypes(enabled = true) {
  const { clientId } = useClient();
  const { data, loading, error, refetch } = useApiCall(clientId && enabled ? `/clients/${clientId}/compliance/evidence/types` : null);
  return {
    data: enabled ? toArray(data).map(normalizeComplianceEvidenceType) : [],
    loading: enabled ? loading : false,
    error: enabled ? error : null,
    refetch,
  };
}

export function useComplianceEvidenceDocuments(options = {}) {
  const { enabled = true, includeArchived = false } = normalizeHookOptions(options);
  const { clientId } = useClient();
  const url = clientId && enabled
    ? `/clients/${clientId}/compliance/evidence?includeArchived=${includeArchived ? 'true' : 'false'}`
    : null;
  const { data, loading, error, refetch } = useApiCall(url);
  return {
    data: enabled ? toArray(data).map(normalizeComplianceEvidenceDocument) : [],
    loading: enabled ? loading : false,
    error: enabled ? error : null,
    refetch,
  };
}

export function useFrameworkControls(frameworkName, options = {}) {
  const { clientId } = useClient();
  const enabled = options.enabled !== false;
  const url = enabled && clientId && frameworkName
    ? `/clients/${clientId}/compliance/controls?framework=${encodeURIComponent(frameworkName)}`
    : null;
  const { data, loading, error } = useApiCall(url);
  return {
    data: enabled ? data ?? null : null,
    loading: enabled ? loading : false,
    error: enabled ? error : null,
  };
}

export function useLatestScanCutoff() {
  const { clientId } = useClient();
  const { data } = useApiCall(clientId ? `/clients/${clientId}/scan-history` : null);
  return useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    const completed = list
      .filter(s => (s.status === 'Completed' || s.completedAt) && s.completedAt)
      .map(s => new Date(s.completedAt).getTime())
      .filter(ts => !Number.isNaN(ts));
    if (completed.length === 0) return null;
    return Math.max(...completed);
  }, [data]);
}

export function useLatestScanJobId() {
  const { clientId } = useClient();
  const { data } = useApiCall(clientId ? `/clients/${clientId}/scan-history` : null);
  return useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    const completed = list
      .filter(s => (s.status === 'Completed' || s.completedAt) && s.completedAt)
      .map(s => ({ id: s.id ?? s.scanId ?? s.scanJobId ?? null, ts: new Date(s.completedAt).getTime() }))
      .filter(x => x.id && !Number.isNaN(x.ts));
    if (completed.length === 0) return null;
    completed.sort((a, b) => b.ts - a.ts);
    return completed[0].id;
  }, [data]);
}

export function useScanHistory(enabled = true) {
  const { clientId } = useClient();
  const { data, loading, error, refetch } = useApiCall(clientId && enabled ? `/clients/${clientId}/scan-history` : null);
  return { data: toArray(data).map(normalizeScanRecord), loading, error, refetch };
}

export function useAttackPaths(enabled = true) {
  const { clientId } = useClient();
  const path = clientId && enabled ? `/clients/${clientId}/attack-paths` : null;
  const { data, loading, error, refetch } = useApiCall(path);
  const problemError = problemErrorFromPayload(data, path);
  return { data: problemError || error ? [] : toArray(data), loading, error: error || problemError, refetch };
}

export function useAssets(enabled = true) {
  const { clientId } = useClient();
  const api = useApiClient();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled && clientId));
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isStale, setIsStale] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = clientId ? `assets-${clientId}` : '';

    if (!enabled || !clientId) {
      setData([]);
      setLoading(false);
      setRefreshing(false);
      setError(null);
      setIsStale(false);
      return () => { cancelled = true; };
    }

    let hasCache = false;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data: cachedData, ts } = JSON.parse(cached);
        if (Array.isArray(cachedData)) {
          hasCache = true;
          setData(cachedData);
          setLoading(false);
          setRefreshing(true);
          setIsStale(Date.now() - Number(ts ?? 0) > 120000);
        }
      }
    } catch (_) {}

    if (!hasCache) {
      setLoading(true);
      setRefreshing(false);
      setIsStale(false);
    }
    setError(null);

    api.get(`/clients/${clientId}/assets`)
      .then(result => {
        if (cancelled) return;
        const assets = toArray(result);
        setData(assets);
        setIsStale(false);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ data: assets, ts: Date.now() }));
        } catch (_) {}
      })
      .catch(err => {
        if (cancelled) return;
        setError(err);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setRefreshing(false);
      });

    return () => { cancelled = true; };
  }, [api, clientId, enabled]);

  return { data: toArray(data).map(normalizeAssetRecord), loading, refreshing, isStale, error };
}

export function useAsset(assetId) {
  const { clientId } = useClient();
  const { data, loading, error } = useApiCall(
    clientId && assetId ? `/clients/${clientId}/assets/${assetId}` : null
  );
  return { data, loading, error };
}

export function useAssetRelationships() {
  const { clientId } = useClient();
  const { data, loading, error, refetch } = useApiCall(clientId ? `/clients/${clientId}/asset-relationships` : null);
  const graph = useMemo(() => normalizeAssetRelationshipGraph(data), [data]);
  return { data: graph, loading, error, refetch };
}

export function useAssetHosts() {
  const { clientId } = useClient();
  const { data, loading, error } = useApiCall(clientId ? `/clients/${clientId}/assets/hosts` : null);
  return { data: toArray(data), loading, error };
}

export function useReports(options = {}) {
  const { clientId } = useClient();
  const enabled = typeof options === 'boolean' ? options : options.enabled !== false;
  const { data, loading, error, refetch } = useApiCall(enabled && clientId ? `/clients/${clientId}/reports?limit=24` : null);
  return {
    data: enabled ? toArray(data) : [],
    loading: enabled ? loading : false,
    error: enabled ? error : null,
    refetch,
  };
}

export function useExposedServices() {
  const { clientId } = useClient();
  const { data, loading, error } = useApiCall(clientId ? `/clients/${clientId}/exposed-services` : null);
  return { data: toArray(data), loading, error };
}

export function useCloudFindings() {
  const { findings: raw, loading, error } = useAllFindings();
  const cloudFindings = useMemo(() => {
    const all = raw.map(normalizeFinding).filter(Boolean);
    return all.filter(isCloudFinding);
  }, [raw]);
  return { data: cloudFindings, total: cloudFindings.length, loading, error };
}

export function useSddFindings() {
  const { clientId } = useClient();
  const { findings: allRaw } = useAllFindings();
  const path = clientId ? `/clients/${clientId}/findings?pageSize=100&category=sdd` : null;
  const sddCall = useApiCall(path);
  const problemError = problemErrorFromPayload(sddCall.data, path);
  const [sddTimedOut, setSddTimedOut] = useState(false);

  useEffect(() => {
    if (!sddCall.loading) {
      setSddTimedOut(false);
      return undefined;
    }
    const timeout = window.setTimeout(() => setSddTimedOut(true), 1400);
    return () => window.clearTimeout(timeout);
  }, [sddCall.loading, path]);

  const fallbackRaw = useMemo(() => {
    return allRaw.filter(f => {
      const module = String(f.module ?? f.sourceModule ?? '').toLowerCase();
      const title = String(f.title ?? '').toLowerCase();
      return isSddFinding(f) || module.includes('sdd') ||
             title.includes('sensitivity label') ||
             title.includes('case matter') ||
             title.includes('financial affidavit') ||
             title.includes('external sharing') ||
             title.includes('sharepoint');
    });
  }, [allRaw]);

  const sddFindings = useMemo(() => {
    const raw = Array.isArray(sddCall.data?.findings)
      ? sddCall.data.findings
      : Array.isArray(sddCall.data) ? sddCall.data : [];
    const shouldUseFallbackOnly = (sddCall.loading && sddTimedOut) || ((sddCall.error || problemError) && fallbackRaw.length > 0);
    const source = shouldUseFallbackOnly ? fallbackRaw : [...raw, ...fallbackRaw];
    const seen = new Set();
    return source
      .filter(f => {
        const id = f.findingId ?? f.persistedFindingId ?? f.id;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map(normalizeFinding)
      .filter(Boolean);
  }, [sddCall.data, sddCall.error, problemError, sddTimedOut, fallbackRaw, sddCall.loading]);

  const hasFallback = fallbackRaw.length > 0;
  const detailError = sddCall.error || problemError;
  const warning = detailError && hasFallback
    ? 'Sensitive data details are showing cached rows while the category endpoint recovers.'
    : sddTimedOut && hasFallback
      ? 'Sensitive data details are still refreshing. Showing cached rows first.'
      : '';

  return {
    data: sddFindings,
    total: sddFindings.length,
    loading: sddCall.loading && !(sddTimedOut && hasFallback),
    detailLoading: sddCall.loading,
    partial: Boolean(warning),
    warning,
    error: (detailError && !hasFallback) ? detailError : null,
    refetch: sddCall.refetch,
  };
}

export function useADFindings() {
  const { clientId, clientName } = useClient();
  const { findings: allRaw, loading: providerLoading, error: providerError } = useAllFindings();
  const path = clientId ? `/clients/${clientId}/findings?pageSize=500&category=ad` : null;
  const adCall = useApiCall(path);
  const problemError = problemErrorFromPayload(adCall.data, path);

  const fallbackRaw = useMemo(() => allRaw.filter(isADFinding), [allRaw]);
  const detailError = adCall.error || problemError;

  const adFindings = useMemo(() => {
    const categoryRaw = Array.isArray(adCall.data?.findings)
      ? adCall.data.findings
      : Array.isArray(adCall.data) ? adCall.data : [];
    const useFallbackOnly = (adCall.loading || detailError) && fallbackRaw.length > 0;
    const source = useFallbackOnly ? fallbackRaw : [...categoryRaw, ...fallbackRaw];
    const seen = new Set();
    const findings = source
      .filter(f => {
        const id = f.findingId ?? f.persistedFindingId ?? f.id;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map(normalizeFinding)
      .filter(Boolean)
      .filter(isADFinding);

    if (!adCall.loading && !detailError && findings.length === 0 && isDemoEnvironmentClient(clientId, clientName)) {
      return demoAdFindings.map(normalizeFinding).filter(Boolean);
    }
    return findings;
  }, [adCall.data, adCall.loading, detailError, fallbackRaw, clientId, clientName]);

  const hasFallback = fallbackRaw.length > 0;
  const loading = adCall.loading && !hasFallback;
  const warning = detailError && hasFallback
    ? 'AD details are showing cached rows while the category endpoint recovers.'
    : adCall.loading && hasFallback
      ? 'AD details are still refreshing. Showing cached rows first.'
      : '';

  return {
    data: adFindings,
    total: adFindings.length,
    loading,
    detailLoading: adCall.loading || providerLoading,
    partial: Boolean(warning),
    warning,
    error: (detailError && !hasFallback) ? detailError : (providerError && !hasFallback ? providerError : null),
    refetch: adCall.refetch,
  };
}

export function useDastFindings() {
  const { findings: raw, loading, error, refetch } = useAllFindings();
  const dastFindings = useMemo(() => {
    const all = raw.map(normalizeFinding).filter(Boolean);
    return all.filter(isDastFinding);
  }, [raw]);
  return { data: dastFindings, total: dastFindings.length, loading, error, refetch };
}

export function useOsintFindings() {
  const { clientId } = useClient();
  const { findings: allRaw } = useAllFindings();
  const osintPath = clientId ? `/clients/${clientId}/findings?pageSize=100&category=osint` : null;
  const breachPath = clientId ? `/clients/${clientId}/findings?pageSize=100&category=breach` : null;
  const osintCall = useApiCall(osintPath);
  const breachCall = useApiCall(breachPath);
  const osintProblem = problemErrorFromPayload(osintCall.data, osintPath);
  const breachProblem = problemErrorFromPayload(breachCall.data, breachPath);
  const [osintTimedOut, setOsintTimedOut] = useState(false);

  useEffect(() => {
    if (!osintCall.loading && !breachCall.loading) {
      setOsintTimedOut(false);
      return undefined;
    }
    const timeout = window.setTimeout(() => setOsintTimedOut(true), 1400);
    return () => window.clearTimeout(timeout);
  }, [osintCall.loading, breachCall.loading, osintPath, breachPath]);

  const fallbackRaw = useMemo(() => {
    return allRaw.filter(isOsintFinding);
  }, [allRaw]);

  const error = osintCall.error || breachCall.error || osintProblem || breachProblem;

  const osintFindings = useMemo(() => {
    const osintRaw = Array.isArray(osintCall.data?.findings) ? osintCall.data.findings
      : Array.isArray(osintCall.data) ? osintCall.data : [];
    const breachRaw = Array.isArray(breachCall.data?.findings) ? breachCall.data.findings
      : Array.isArray(breachCall.data) ? breachCall.data : [];
    const shouldUseFallbackOnly = ((osintCall.loading || breachCall.loading) && osintTimedOut) || (error && fallbackRaw.length > 0);
    const combined = shouldUseFallbackOnly ? fallbackRaw : [...osintRaw, ...breachRaw, ...fallbackRaw];
    // Dedupe by finding ID
    const seen = new Set();
    const unique = combined.filter(f => {
      const id = f.findingId ?? f.persistedFindingId ?? f.id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return unique.map(normalizeFinding).filter(Boolean).filter(isOsintFinding);
  }, [osintCall.data, breachCall.data, fallbackRaw, error, osintCall.loading, breachCall.loading, osintTimedOut]);

  const hasFallback = fallbackRaw.length > 0;
  const loading = (osintCall.loading || breachCall.loading) && !(osintTimedOut && hasFallback);
  const warning = error && hasFallback
    ? 'OSINT details are showing cached rows while the category endpoint recovers.'
    : osintTimedOut && hasFallback
      ? 'OSINT details are still refreshing. Showing cached rows first.'
      : '';

  const refetch = useCallback(() => {
    osintCall.refetch();
    breachCall.refetch();
  }, [osintCall.refetch, breachCall.refetch]);

  return {
    data: osintFindings,
    total: osintFindings.length,
    loading,
    detailLoading: osintCall.loading || breachCall.loading,
    partial: Boolean(warning),
    warning,
    error: (error && !hasFallback) ? error : null,
    refetch,
  };
}

export function useClientModules() {
  const { clientId } = useClient();
  const { data, loading } = useApiCall(clientId ? `/clients/${clientId}/modules` : null);
  const modules = useMemo(() => {
    if (!data) return { m365: false, ad: false };
    const list = Array.isArray(data) ? data : data?.modules ?? [];
    return {
      m365: list.some(m => (m.moduleKey ?? m.name ?? '').toLowerCase().includes('m365') && m.isEnabled),
      ad: list.some(m => (m.moduleKey ?? m.name ?? '').toLowerCase().startsWith('ad-') && m.isEnabled),
    };
  }, [data]);
  return { ...modules, loading };
}

export function useScanSchedule() {
  const { clientId } = useClient();
  const path = clientId ? `/clients/${clientId}/scan-schedule` : null;
  const { data, loading, error, refetch } = useApiCall(path);
  const problemError = problemErrorFromPayload(data, path);
  return { data: error || problemError ? null : data, loading, error: error || problemError, refetch };
}

export function useScanScheduleHistory() {
  const { clientId } = useClient();
  const path = clientId ? `/clients/${clientId}/scan-schedule/history` : null;
  const { data, loading, error, refetch } = useApiCall(path);
  const problemError = problemErrorFromPayload(data, path);
  return {
    data: problemError || error ? [] : normalizeSchedulerHistory(data),
    loading,
    error: error || problemError,
    refetch,
  };
}

export function useAttackChains() {
  const { clientId, clientName } = useClient();
  const path = clientId ? `/clients/${clientId}/attack-chains` : null;
  const { data, loading, error, refetch } = useApiCall(path);
  const problemError = problemErrorFromPayload(data, path);
  const normalizedError = error || problemError;
  const chains = normalizedError ? [] : toArray(data);
  const resolvedChains = !normalizedError && chains.length === 0 && isDemoEnvironmentClient(clientId, clientName)
    ? demoAttackChains
    : chains;
  return { data: resolvedChains, loading, error: normalizedError, refetch };
}

export function useOnboardingStatus(enabled = true) {
  const { clientId } = useClient();
  const { data, loading, error, refetch } = useApiCall(clientId && enabled ? `/clients/${clientId}/onboarding-status` : null);
  return { data, loading, error, refetch };
}

export function useActiveScan() {
  const { clientId } = useClient();
  const { data } = useApiCall(
    clientId ? `/clients/${clientId}/scan-history?limit=1` : null
  );
  const latest = Array.isArray(data) ? data[0] : data?.scans?.[0] ?? null;
  const isRunning = latest?.status === 'Running' || latest?.status === 'Queued';
  return { isRunning, latest };
}
